import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, Clock, AlertTriangle, Calendar, Upload, FileText,
  MessageSquare, Mic, ChevronRight, ChevronLeft, X, Loader2,
  Send, Paperclip, CheckSquare, Circle, AlertCircle
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const TASK_TYPES = {
  approval: { label: 'Approval Needed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  upload: { label: 'Upload Requested', color: 'bg-blue-500/20 text-blue-400', icon: Upload },
  availability: { label: 'Availability Request', color: 'bg-purple-500/20 text-purple-400', icon: Calendar },
  recording: { label: 'Recording Needed', color: 'bg-orange-500/20 text-orange-400', icon: Mic },
  decision: { label: 'Decision Required', color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
  general: { label: 'Task', color: 'bg-gray-500/20 text-gray-400', icon: FileText }
};

const STATUSES = {
  not_started: { label: 'Not Started', color: 'bg-gray-500/20 text-gray-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
  submitted: { label: 'Submitted', color: 'bg-green-500/20 text-green-400' },
  needs_clarification: { label: 'Needs Clarification', color: 'bg-orange-500/20 text-orange-400' },
  completed: { label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400' },
  overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400' }
};

const AETasks = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [response, setResponse] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userEmail) {
      fetchTasks();
    }
  }, [userEmail]);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async () => {
    if (!selectedTask || (!response.trim() && files.length === 0)) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/always-elevate/tasks/${selectedTask.id}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'submitted',
          response: response,
          files: files.map(f => f.name)
        })
      });
      const data = await res.json();
      if (data.success) {
        setResponse('');
        setFiles([]);
        fetchTasks();
        // Update selected task
        setSelectedTask(prev => prev ? { ...prev, status: 'submitted', response } : null);
      }
    } catch (err) {
      console.error('Error submitting response:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['not_started', 'in_progress', 'needs_clarification'].includes(task.status);
    if (filter === 'completed') return task.status === 'completed' || task.status === 'submitted';
    return task.task_type === filter;
  });

  const isOverdue = (task) => {
    return task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed' && task.status !== 'submitted';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }}>
                My Tasks
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {tasks.filter(t => t.status !== 'completed' && t.status !== 'submitted').length} tasks pending
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'All Tasks' },
            { id: 'pending', label: 'Pending' },
            { id: 'approval', label: 'Approvals' },
            { id: 'upload', label: 'Uploads' },
            { id: 'availability', label: 'Availability' },
            { id: 'completed', label: 'Completed' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                filter === f.id 
                  ? 'bg-[#C4A052] text-black font-medium' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Task List */}
          <div className="lg:col-span-1 space-y-3">
            {filteredTasks.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400 opacity-50" />
                <p className="text-gray-400">No tasks found</p>
              </div>
            ) : (
              filteredTasks.map(task => {
                const taskType = TASK_TYPES[task.task_type] || TASK_TYPES.general;
                const TaskIcon = taskType.icon;
                const overdue = isOverdue(task);
                const status = overdue ? STATUSES.overdue : (STATUSES[task.status] || STATUSES.not_started);
                
                return (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                      selectedTask?.id === task.id ? 'border-[#C4A052]' : 'border-white/10'
                    } ${overdue ? 'border-red-500/50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${taskType.color}`}>
                        <TaskIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm truncate">{task.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>
                            {status.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        {task.due_date && (
                          <p className={`text-xs mt-2 ${overdue ? 'text-red-400' : 'text-gray-500'}`}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Task Detail */}
          <div className="lg:col-span-2">
            {selectedTask ? (
              <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
                {/* Task Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${TASK_TYPES[selectedTask.task_type]?.color || TASK_TYPES.general.color}`}>
                          {TASK_TYPES[selectedTask.task_type]?.label || 'Task'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          selectedTask.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                          selectedTask.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {selectedTask.priority} priority
                        </span>
                      </div>
                      <h2 className="text-xl font-bold">{selectedTask.title}</h2>
                    </div>
                    <button 
                      onClick={() => setSelectedTask(null)}
                      className="p-2 hover:bg-white/10 rounded-lg lg:hidden"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                    {selectedTask.due_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Due {new Date(selectedTask.due_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Circle className="w-4 h-4" />
                      <span className="capitalize">{selectedTask.status?.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>

                {/* Task Content */}
                <div className="p-6 space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</h3>
                    <p className="text-gray-300">{selectedTask.description || 'No description provided.'}</p>
                  </div>

                  {/* Instructions */}
                  {selectedTask.instructions && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Instructions</h3>
                      <div className="bg-white/5 rounded-lg p-4 text-sm text-gray-300">
                        {selectedTask.instructions}
                      </div>
                    </div>
                  )}

                  {/* Previous Response */}
                  {selectedTask.response && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Your Response</h3>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-sm text-gray-300">
                        {selectedTask.response}
                      </div>
                    </div>
                  )}

                  {/* Response Form */}
                  {selectedTask.status !== 'completed' && selectedTask.status !== 'submitted' && (
                    <div className="border-t border-white/10 pt-6">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Your Response</h3>
                      
                      {/* Text Response */}
                      <textarea
                        value={response}
                        onChange={(e) => setResponse(e.target.value)}
                        placeholder="Type your response here..."
                        className="w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-sm resize-none h-32 focus:outline-none focus:border-[#C4A052]"
                      />

                      {/* File Upload */}
                      <div className="mt-4">
                        <input
                          type="file"
                          id="file-upload"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <label
                          htmlFor="file-upload"
                          className="flex items-center gap-2 px-4 py-2 bg-black border border-white/20 rounded-lg cursor-pointer hover:bg-white/10 transition-colors text-sm text-gray-400 w-fit"
                        >
                          <Paperclip className="w-4 h-4" />
                          Attach Files
                        </label>

                        {/* File List */}
                        {files.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {files.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm">{file.name}</span>
                                </div>
                                <button onClick={() => removeFile(idx)} className="p-1 hover:bg-white/10 rounded">
                                  <X className="w-4 h-4 text-gray-400" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={handleSubmitResponse}
                          disabled={submitting || (!response.trim() && files.length === 0)}
                          className="flex items-center gap-2 px-6 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          Submit Response
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Completed Message */}
                  {(selectedTask.status === 'completed' || selectedTask.status === 'submitted') && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <p className="text-green-400">
                        {selectedTask.status === 'submitted' 
                          ? 'Response submitted - awaiting review' 
                          : 'This task has been completed'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Select a Task</h3>
                <p className="text-gray-500 text-sm">Click on a task from the list to view details and respond</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AETasks;
